/**
 * ============================================================================
 * ItemManagerSheet — S19.10 + Fase 4 UX (Resize + Validación + Unidad/Peso/CBM)
 * ============================================================================
 * Mejoras:
 *  - Anchos mínimos en columnas numéricas (no se cortan los valores).
 *  - Formato monetario USD para FOB y Sub-Total ($#,##0.00).
 *  - Validación de inputs numéricos: bloquea texto, marca inválido en rojo.
 *  - Nuevas columnas: Unidad (select), Peso (kg), Volumen (CBM).
 *  - Indicador visual de edición: ring + tooltip "Modificado".
 *  - Badge "USD" fijo junto al FOB.
 * ============================================================================
 */

import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, RotateCcw, Package, Copy } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { EngineItemInput, BenefitMatrix } from "@/lib/landedCostEngine";
import type {
  SimulatedItem,
  SimulatedItemOverride,
  SimulatedUnidad,
} from "@/hooks/useProformaSimulations";
import { BenefitAduaneroSelect, resolveBenefitValue } from "@/components/BenefitAduaneroSelect";
import { lookupArancel } from "@/lib/arancelLookup";
import { CurrencyCombobox } from "@/components/CurrencyCombobox";
import { isVisible, shouldForceAdvanced } from "@/lib/simulatorUiMode";
import { Switch } from "@/components/ui/switch";
import { AlertTriangle } from "lucide-react";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** UI mode: en "quick" se ocultan columnas avanzadas. */
  uiMode?: import("@/lib/simulatorUiMode").UiMode;
  onUiModeChange?: (m: import("@/lib/simulatorUiMode").UiMode) => void;
  baseItems: EngineItemInput[];
  hostDocumentoId: string | null;
  baseArancelMap: Record<string, number>;
  baseIvaMap: Record<string, number>;
  trmEfectivaMap?: Record<string, number>;
  initialEditados: Record<string, SimulatedItemOverride>;
  initialNuevos: SimulatedItem[];
  initialArancelOverrides: Record<string, number>;
  initialIvaOverrides: Record<string, number>;
  initialBeneficioOverrides: Record<string, BenefitMatrix>;
  onApply: (params: {
    itemsEditados: Record<string, SimulatedItemOverride>;
    itemsNuevos: SimulatedItem[];
    arancelOverrides: Record<string, number>;
    ivaOverrides: Record<string, number>;
    beneficioOverrides: Record<string, BenefitMatrix>;
  }) => void;
}

const UNIDADES: SimulatedUnidad[] = ["UN", "KG", "CBM", "LTS", "PAR"];

const fmtUSD = (n: number) =>
  `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtMoney = (n: number, moneda: string) =>
  `${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${moneda}`;

const fmtCOP = (n: number) =>
  `${Math.round(n).toLocaleString("es-CO")} COP`;

const fmtNum = (n: number) =>
  n.toLocaleString("en-US", { maximumFractionDigits: 2 });

function newId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `sim-item-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

const num = (v: unknown, fallback = 0): number => {
  if (v === null || v === undefined || v === "") return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

/** Acepta solo dígitos, punto/coma decimal y signo negativo. */
const isValidNumericInput = (raw: string): boolean => {
  if (raw === "" || raw === "-") return true;
  return /^-?\d*([.,]\d*)?$/.test(raw);
};

/** Convierte input string a number; coma → punto. */
const parseNumeric = (raw: string): number => {
  const norm = raw.replace(",", ".").replace(/[^\d.-]/g, "");
  return num(norm);
};

/**
 * Input numérico con validación visual. Marca rojo si el usuario tipea letras
 * y muestra tooltip de error. Bloquea valores no parseables.
 */
function NumericCell({
  value,
  onChange,
  edited,
  className = "",
  step = "any",
  min,
  align = "right",
}: {
  value: number;
  onChange: (n: number) => void;
  edited?: boolean;
  className?: string;
  step?: string;
  min?: number;
  align?: "right" | "left";
}) {
  const [raw, setRaw] = useState<string>(String(value ?? 0));
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    setRaw(String(value ?? 0));
  }, [value]);

  const handle = (next: string) => {
    setRaw(next);
    if (!isValidNumericInput(next)) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    const parsed = parseNumeric(next);
    if (min !== undefined && parsed < min) return;
    onChange(parsed);
  };

  const baseClasses = `h-8 text-[13px] tabular-nums font-mono ${
    align === "right" ? "text-right" : "text-left"
  }`;
  const editedRing = edited ? "ring-1 ring-primary border-primary/40 bg-primary/5 font-semibold" : "";
  const invalidRing = invalid ? "ring-1 ring-destructive border-destructive/60" : "";

  const input = (
    <Input
      type="text"
      inputMode="decimal"
      step={step}
      value={raw}
      onChange={(e) => handle(e.target.value)}
      onBlur={() => {
        if (invalid) {
          setRaw(String(value ?? 0));
          setInvalid(false);
        }
      }}
      className={`${baseClasses} ${editedRing} ${invalidRing} ${className}`}
    />
  );

  if (!invalid && !edited) return input;

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>{input}</TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          {invalid ? "Valor inválido — solo se permiten números" : "Modificado en esta simulación"}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function ItemManagerSheet({
  open,
  onOpenChange,
  uiMode = "advanced",
  onUiModeChange,
  baseItems,
  hostDocumentoId,
  baseArancelMap,
  baseIvaMap,
  trmEfectivaMap,
  initialEditados,
  initialNuevos,
  initialArancelOverrides,
  initialIvaOverrides,
  initialBeneficioOverrides,
  onApply,
}: Props) {
  const [editados, setEditados] = useState<Record<string, SimulatedItemOverride>>({});
  const [nuevos, setNuevos] = useState<SimulatedItem[]>([]);
  const [arancelOv, setArancelOv] = useState<Record<string, number>>({});
  const [ivaOv, setIvaOv] = useState<Record<string, number>>({});
  const [beneficioOv, setBeneficioOv] = useState<Record<string, BenefitMatrix>>({});
  const [confirmDelete, setConfirmDelete] = useState<{ id: string; sku: string; isBase: boolean } | null>(null);

  const disableBaseItem = (id: string) => {
    setEditados((prev) => {
      const current = { ...(prev[id] ?? {}) } as Record<string, unknown>;
      current.aplica_costeo_inventario = false;
      return { ...prev, [id]: current as SimulatedItemOverride };
    });
  };

  const duplicateItem = (
    source: {
      sku: string;
      descripcion: string;
      unidad: SimulatedUnidad;
      cantidad: number;
      precio_unitario: number;
      peso_bruto_kg: number;
      volumen_cbm: number;
      hs_code: string;
      grados_alcoholicos: number;
      litros_por_unidad: number;
      tarifa_ico: number;
      ico_tipo: string | null;
      beneficio_aduanero: BenefitMatrix;
      moneda: string;
    },
  ) => {
    const id = newId();
    const subTotal = num(source.cantidad) * num(source.precio_unitario);
    setNuevos((prev) => [
      ...prev,
      {
        id,
        documento_id: hostDocumentoId ?? "synthetic-doc",
        sku: source.sku ? `${source.sku} (copia)` : "(copia)",
        descripcion: source.descripcion,
        unidad: source.unidad,
        cantidad: source.cantidad,
        precio_unitario: source.precio_unitario,
        sub_total: subTotal,
        peso_bruto_kg: source.peso_bruto_kg,
        volumen_cbm: source.volumen_cbm,
        hs_code: source.hs_code,
        grados_alcoholicos: source.grados_alcoholicos,
        litros_por_unidad: source.litros_por_unidad,
        tarifa_ico: source.tarifa_ico,
        ico_tipo: source.ico_tipo,
        aplica_tlc: source.beneficio_aduanero === "TLC_PURO",
        beneficio_aduanero: source.beneficio_aduanero,
        moneda: source.moneda,
      },
    ]);
    setBeneficioOv((prev) => ({ ...prev, [id]: source.beneficio_aduanero }));
  };

  useEffect(() => {
    if (open) {
      setEditados({ ...initialEditados });
      setNuevos([...initialNuevos]);
      setArancelOv({ ...initialArancelOverrides });
      setIvaOv({ ...initialIvaOverrides });
      setBeneficioOv({ ...initialBeneficioOverrides });
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Helpers ítems base ─────────────────────────────────────
  const setBaseField = (
    id: string,
    field: keyof SimulatedItemOverride,
    parsed: number,
    original: number,
    extra?: { recalcSubTotal?: boolean; cantidadOriginal?: number; fobOriginal?: number },
  ) => {
    setEditados((prev) => {
      const current = { ...(prev[id] ?? {}) };
      if (Math.abs(parsed - original) < 0.005) {
        delete (current as Record<string, unknown>)[field];
      } else {
        (current as Record<string, unknown>)[field] = parsed;
      }
      if (extra?.recalcSubTotal) {
        const cantidad = current.cantidad ?? extra.cantidadOriginal ?? 0;
        const precio = current.precio_unitario ?? extra.fobOriginal ?? 0;
        const newSub = num(cantidad) * num(precio);
        const baseSub = num(extra.cantidadOriginal) * num(extra.fobOriginal);
        if (Math.abs(newSub - baseSub) < 0.005) {
          delete current.sub_total;
        } else {
          current.sub_total = newSub;
        }
      }
      const next = { ...prev };
      if (Object.keys(current).length === 0) {
        delete next[id];
      } else {
        next[id] = current;
      }
      return next;
    });
  };

  const setBaseUnidad = (id: string, value: SimulatedUnidad, original: SimulatedUnidad) => {
    setEditados((prev) => {
      const current = { ...(prev[id] ?? {}) };
      if (value === original) {
        delete (current as Record<string, unknown>).unidad_medida;
      } else {
        (current as Record<string, unknown>).unidad_medida = value;
      }
      const next = { ...prev };
      if (Object.keys(current).length === 0) delete next[id];
      else next[id] = current;
      return next;
    });
  };

  const setBaseMoneda = (id: string, value: string, original: string) => {
    setEditados((prev) => {
      const current = { ...(prev[id] ?? {}) } as Record<string, unknown>;
      if (value.toUpperCase() === original.toUpperCase()) {
        delete current.moneda;
      } else {
        current.moneda = value.toUpperCase();
      }
      const next = { ...prev };
      if (Object.keys(current).length === 0) delete next[id];
      else next[id] = current as SimulatedItemOverride;
      return next;
    });
  };

  /** Override del tipo de ICO ('VEHICULOS' | 'CIGARRILLOS' | null/'' = legacy bebidas). */
  const setBaseIcoTipo = (id: string, value: string | null, original: string | null) => {
    const norm = (v: string | null) => (v ?? "").toUpperCase();
    setEditados((prev) => {
      const current = { ...(prev[id] ?? {}) } as Record<string, unknown>;
      if (norm(value) === norm(original)) {
        delete current.ico_tipo;
      } else {
        current.ico_tipo = value && value.length > 0 ? value : null;
      }
      const next = { ...prev };
      if (Object.keys(current).length === 0) delete next[id];
      else next[id] = current as SimulatedItemOverride;
      return next;
    });
  };

  const setRatePct = (
    map: "arancel" | "iva",
    id: string,
    parsed: number,
    base: number,
  ) => {
    const setter = map === "arancel" ? setArancelOv : setIvaOv;
    setter((prev) => {
      const next = { ...prev };
      if (Math.abs(parsed - base) < 0.005) {
        delete next[id];
      } else {
        next[id] = parsed;
      }
      return next;
    });
  };

  const setBenefit = (id: string, next: BenefitMatrix, baseValue?: BenefitMatrix) => {
    setBeneficioOv((prev) => {
      const out = { ...prev };
      if (baseValue && next === baseValue) delete out[id];
      else out[id] = next;
      return out;
    });

    // Fase 2 hotfix — Matriz de Beneficios (§22 ARCHITECTURE):
    // al cambiar el régimen, sincronizamos los overrides de Arancel/IVA
    // para que el motor recalcule correctamente sin esperar otra acción.
    if (next === "GENERAL") {
      // Eliminar overrides → el RPC vuelve a usar el lookup de arancel_1881.
      setArancelOv((p) => { const n = { ...p }; delete n[id]; return n; });
      setIvaOv((p) => { const n = { ...p }; delete n[id]; return n; });
    } else if (next === "TLC_PURO") {
      // Arancel 0%; IVA conserva el lookup vigente.
      setArancelOv((p) => ({ ...p, [id]: 0 }));
      setIvaOv((p) => { const n = { ...p }; delete n[id]; return n; });
    } else if (next === "LEY_1715" || next === "ZONA_FRANCA") {
      // Arancel + IVA forzados a 0.
      setArancelOv((p) => ({ ...p, [id]: 0 }));
      setIvaOv((p) => ({ ...p, [id]: 0 }));
    }
  };

  // Local arancel_1881 lookup so % Arancel / % IVA fields populate
  // the moment the user finishes typing an HS code, instead of waiting
  // for the RPC to fire on "Aplicar al escenario".
  const applyHsLookup = (id: string, rawCode: string) => {
    const clean = (rawCode ?? "").replace(/\D/g, "");
    if (clean.length < 6) return;
    void lookupArancel(clean).then((res) => {
      if (!res.matched) return;
      setArancelOv((p) => (p[id] !== undefined ? p : { ...p, [id]: res.gravamen_porcentaje }));
      setIvaOv((p) => (p[id] !== undefined ? p : { ...p, [id]: res.iva_porcentaje }));
    });
  };

  const resetRow = (id: string) => {
    setEditados((p) => { const n = { ...p }; delete n[id]; return n; });
    setArancelOv((p) => { const n = { ...p }; delete n[id]; return n; });
    setIvaOv((p) => { const n = { ...p }; delete n[id]; return n; });
    setBeneficioOv((p) => { const n = { ...p }; delete n[id]; return n; });
  };

  // ── Ítems nuevos ─────────────────────────────────────
  const addNuevo = () => {
    const id = newId();
    setNuevos((prev) => [
      ...prev,
      {
        id,
        documento_id: hostDocumentoId ?? "synthetic-doc",
        sku: "",
        descripcion: "",
        unidad: "UN",
        cantidad: 1,
        precio_unitario: 0,
        sub_total: 0,
        peso_bruto_kg: 0,
        volumen_cbm: 0,
        hs_code: "",
        grados_alcoholicos: 0,
        litros_por_unidad: 0,
        tarifa_ico: 0,
        ico_tipo: null,
        aplica_tlc: false,
        beneficio_aduanero: "GENERAL",
        moneda: "USD",
      },
    ]);
    // No forzamos defaults de arancel/IVA: si el usuario deja los campos en
    // blanco, el RPC los resuelve desde arancel_1881 usando el hs_code del ítem.
    setBeneficioOv((prev) => ({ ...prev, [id]: "GENERAL" }));
  };

  const updateNuevo = <K extends keyof SimulatedItem>(
    id: string,
    key: K,
    value: SimulatedItem[K],
  ) => {
    setNuevos((prev) =>
      prev.map((it) => {
        if (it.id !== id) return it;
        const next = { ...it, [key]: value };
        if (key === "cantidad" || key === "precio_unitario") {
          next.sub_total = num(next.cantidad) * num(next.precio_unitario);
        }
        return next;
      }),
    );
  };

  const removeNuevo = (id: string) => {
    setNuevos((prev) => prev.filter((g) => g.id !== id));
    setArancelOv((p) => { const n = { ...p }; delete n[id]; return n; });
    setIvaOv((p) => { const n = { ...p }; delete n[id]; return n; });
    setBeneficioOv((p) => { const n = { ...p }; delete n[id]; return n; });
  };

  const editedCount = useMemo(
    () =>
      Object.keys(editados).length +
      Object.keys(arancelOv).filter((id) => baseItems.some((b) => b.id === id)).length +
      Object.keys(ivaOv).filter((id) => baseItems.some((b) => b.id === id)).length +
      Object.keys(beneficioOv).filter((id) => baseItems.some((b) => b.id === id)).length,
    [editados, arancelOv, ivaOv, beneficioOv, baseItems],
  );

  const nuevosValidos = nuevos.filter(
    (it) => num(it.cantidad) > 0 && num(it.precio_unitario) >= 0 && (it.sku?.trim() || it.descripcion?.trim()),
  );

  const handleApply = () => {
    onApply({
      itemsEditados: editados,
      itemsNuevos: nuevosValidos,
      arancelOverrides: arancelOv,
      ivaOverrides: ivaOv,
      beneficioOverrides: beneficioOv,
    });
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[1100px] p-0 flex flex-col gap-0"
      >
        <SheetHeader className="px-6 py-4 border-b">
          <div className="flex items-center justify-between gap-2">
            <div className="space-y-1">
              <SheetTitle className="flex items-center gap-2 text-base">
                <Package className="h-4 w-4 text-primary" />
                Gestor de Ítems del Escenario
              </SheetTitle>
              <SheetDescription className="text-xs">
                Edita cantidades, FOB, peso, volumen y % de aranceles. Los cambios solo afectan a esta simulación.
              </SheetDescription>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {editedCount > 0 && (
                <Badge variant="outline" className="text-[10px]">
                  {editedCount} edit.
                </Badge>
              )}
              {nuevosValidos.length > 0 && (
                <Badge variant="outline" className="text-[10px] border-primary/30 text-primary">
                  +{nuevosValidos.length} nuevo{nuevosValidos.length === 1 ? "" : "s"}
                </Badge>
              )}
              {onUiModeChange && (
                <TooltipProvider delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-2 pl-3 pr-4 border-l border-r">
                        <span className="text-xs text-muted-foreground">Avanzado</span>
                        <Switch
                          checked={uiMode === "advanced"}
                          onCheckedChange={(v) => onUiModeChange(v ? "advanced" : "quick")}
                          aria-label="Alternar modo Rápido / Avanzado"
                        />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="text-xs max-w-[220px]">
                      Mostrar columnas de peso, volumen, ICO y aranceles
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </div>
          {(() => {
            const allItems = [...baseItems, ...nuevos];
            const force = shouldForceAdvanced(allItems as never[], []);
            if (!force.forced || uiMode === "advanced") return null;
            return (
              <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-amber-900 dark:text-amber-200">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <div className="text-[11px] leading-snug flex-1">
                  <span className="font-semibold">Recomendamos modo Avanzado</span>
                  <span className="opacity-80"> — Hay ítems con ICO o régimen especial.</span>
                </div>
                {onUiModeChange && (
                  <button
                    type="button"
                    onClick={() => onUiModeChange("advanced")}
                    className="text-[11px] underline font-medium shrink-0"
                  >
                    Cambiar a Avanzado
                  </button>
                )}
              </div>
            );
          })()}
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          <div className="px-6 py-4 space-y-3">
            {/* ── Lista única de ítems del escenario (Fase 2 V3) ── */}
            <section className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Ítems del escenario
                </h3>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-muted-foreground tabular-nums">
                    {baseItems.length + nuevos.length} ítem{(baseItems.length + nuevos.length) === 1 ? "" : "s"}
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 gap-1.5"
                    onClick={addNuevo}
                    disabled={!hostDocumentoId}
                    title={!hostDocumentoId ? "El snapshot no tiene documento host" : undefined}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Añadir ítem
                  </Button>
                </div>
              </div>

              {(baseItems.length + nuevos.length) === 0 ? (
                <div className="rounded-md border border-dashed py-6 text-center text-xs text-muted-foreground">
                  Sin ítems en el escenario. Usa "Añadir ítem" para crear el primero.
                </div>
              ) : (
                <div className="space-y-2">
                  {/* Lista plana: ítems del snapshot primero, nuevos al final */}
                  {[
                    ...baseItems.map((b) => ({ kind: "base" as const, it: b })),
                    ...nuevos.map((n) => ({ kind: "new" as const, it: n })),
                  ].map(({ kind, it }) => {
                    const isBase = kind === "base";
                    // ─── BASE: leer original + override ───
                    const baseIt = isBase ? (it as EngineItemInput) : null;
                    const newIt = !isBase ? (it as SimulatedItem) : null;
                    const ed = isBase ? (editados[it.id] ?? {}) : {};

                    const cantidadOrig = isBase ? num(baseIt!.cantidad) : 0;
                    const fobOrig = isBase ? num(baseIt!.precio_unitario) : 0;
                    const subOrig = isBase ? num(baseIt!.sub_total) : 0;
                    const pesoOrig = isBase ? num(baseIt!.peso_bruto_kg) : 0;
                    const cbmOrig = isBase ? num((baseIt as any).volumen_cbm) : 0;
                    const gradosOrig = isBase ? num((baseIt as any).grados_alcoholicos) : 0;
                    const litrosOrig = isBase ? num((baseIt as any).litros_por_unidad) : 0;
                    const tarifaIcoOrig = isBase ? num((baseIt as any).tarifa_ico) : 0;
                    const unidadOrig = isBase
                      ? (((baseIt!.unidad_medida as SimulatedUnidad | null) || "UN") as SimulatedUnidad)
                      : "UN";
                    const monedaOrig = isBase
                      ? (((baseIt as { moneda?: string | null }).moneda) || "USD").toUpperCase()
                      : "USD";
                    // BUG 2 fix — para ítems nuevos, también consultar baseArancelMap/
                    // baseIvaMap por id (el padre lo alimenta con valores resueltos
                    // por el RPC vía hs_code), no asumir 0%/19%.
                    const arancelBase = isBase
                      ? num(baseArancelMap[it.id], 0)
                      : num(baseArancelMap[it.id], 0);
                    const ivaBase = isBase
                      ? num(baseIvaMap[it.id], num((baseIt as any).iva_override, 19))
                      : num(baseIvaMap[it.id], 19);
                    const benefitBase = isBase
                      ? resolveBenefitValue((baseIt as any).beneficio_aduanero, (baseIt as any).aplica_tlc)
                      : "GENERAL";

                    // ─── Valor efectivo a mostrar ───
                    const sku = isBase ? (baseIt!.sku ?? "") : (newIt!.sku ?? "");
                    const descripcion = isBase ? (baseIt!.descripcion ?? "") : (newIt!.descripcion ?? "");
                    const hsCode = isBase
                      ? ((ed as { hs_code?: string | null }).hs_code ?? (baseIt as { hs_code?: string | null }).hs_code ?? "")
                      : ((newIt as { hs_code?: string | null }).hs_code ?? "");
                    const cantidad = isBase ? (ed.cantidad ?? cantidadOrig) : num(newIt!.cantidad);
                    const fob = isBase ? (ed.precio_unitario ?? fobOrig) : num(newIt!.precio_unitario);
                    const peso = isBase ? (ed.peso_bruto_kg ?? pesoOrig) : num(newIt!.peso_bruto_kg);
                    const cbm = isBase ? (ed.volumen_cbm ?? cbmOrig) : num((newIt as any).volumen_cbm);
                    const grados = isBase
                      ? ((ed as any).grados_alcoholicos ?? gradosOrig)
                      : num((newIt as any).grados_alcoholicos);
                    const litros = isBase
                      ? ((ed as any).litros_por_unidad ?? litrosOrig)
                      : num((newIt as any).litros_por_unidad);
                    const tarifaIco = isBase
                      ? ((ed as any).tarifa_ico ?? tarifaIcoOrig)
                      : num((newIt as any).tarifa_ico);
                    const icoTipoOrig = isBase
                      ? (((baseIt as any).ico_tipo ?? null) as string | null)
                      : null;
                    const icoTipo: string | null = isBase
                      ? ((ed as any).ico_tipo !== undefined
                          ? ((ed as any).ico_tipo as string | null)
                          : icoTipoOrig)
                      : (((newIt as any).ico_tipo ?? null) as string | null);
                    const isIcoVehiculos = (icoTipo ?? "").toUpperCase() === "VEHICULOS";
                    const unidad = isBase
                      ? ((ed.unidad_medida as SimulatedUnidad | undefined) ?? unidadOrig)
                      : (((newIt!.unidad as SimulatedUnidad | null) ?? "UN") as SimulatedUnidad);
                    const moneda = isBase
                      ? (((ed as { moneda?: string }).moneda ?? monedaOrig).toUpperCase())
                      : ((newIt!.moneda ?? "USD").toString().toUpperCase());
                    const subProj = num(cantidad) * num(fob);
                    const arancel = arancelOv[it.id] ?? arancelBase;
                    const iva = ivaOv[it.id] ?? ivaBase;
                    const benefit = beneficioOv[it.id] ?? benefitBase;
                    const dirty = isBase
                      ? (editados[it.id] !== undefined ||
                         arancelOv[it.id] !== undefined ||
                         ivaOv[it.id] !== undefined ||
                         beneficioOv[it.id] !== undefined)
                      : false;

                    return (
                      <div
                        key={it.id}
                        className={`rounded-md border bg-card p-3 space-y-2 ${dirty ? "border-primary/40 ring-1 ring-primary/20" : ""}`}
                      >
                        <div className="flex items-start gap-2">
                          <div className="flex-1 grid grid-cols-12 gap-2">
                            <div className="col-span-12 md:col-span-3 space-y-1">
                              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">SKU</Label>
                              {isBase ? (
                                <div className="h-8 px-2 flex items-center rounded-md border bg-muted/30 text-[13px] font-mono truncate">
                                  {sku || "(sin SKU)"}
                                </div>
                              ) : (
                                <Input
                                  value={sku}
                                  onChange={(e) => updateNuevo(it.id, "sku", e.target.value)}
                                  placeholder="Ej. SKU-9001"
                                  maxLength={64}
                                  className="h-8 text-[13px] font-mono"
                                />
                              )}
                            </div>
                            <div className="col-span-12 md:col-span-3 space-y-1">
                              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Descripción</Label>
                              {isBase ? (
                                <div className="h-8 px-2 flex items-center rounded-md border bg-muted/30 text-[13px] truncate">
                                  {descripcion || "—"}
                                </div>
                              ) : (
                                <Input
                                  value={descripcion}
                                  onChange={(e) => updateNuevo(it.id, "descripcion", e.target.value)}
                                  placeholder="Detalle del ítem"
                                  maxLength={255}
                                  className="h-8 text-[13px]"
                                />
                              )}
                            </div>
                            <div className="col-span-6 md:col-span-2 space-y-1">
                              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">HS Code</Label>
                              <Input
                                value={hsCode}
                                onChange={(e) => {
                                  const next = e.target.value.trim();
                                  if (isBase) {
                                    const original = ((baseIt as { hs_code?: string | null }).hs_code ?? "").trim();
                                    setEditados((prev) => {
                                      const current = { ...(prev[it.id] ?? {}) } as Record<string, unknown>;
                                      if (next === original) delete current.hs_code;
                                      else current.hs_code = next;
                                      const out = { ...prev };
                                      if (Object.keys(current).length === 0) delete out[it.id];
                                      else out[it.id] = current as SimulatedItemOverride;
                                      return out;
                                    });
                                  } else {
                                    updateNuevo(it.id, "hs_code" as keyof SimulatedItem, next as never);
                                  }
                                   applyHsLookup(it.id, next);
                                 }}
                                placeholder="2204210000"
                                maxLength={14}
                                className={`h-8 text-[13px] font-mono ${isBase && (ed as { hs_code?: string | null }).hs_code !== undefined ? "ring-1 ring-primary border-primary/40 bg-primary/5 font-semibold" : ""}`}
                              />
                            </div>
                            {isVisible(uiMode, "unidad") && (
                              <div className="col-span-4 md:col-span-2 space-y-1">
                                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Unidad</Label>
                                <Select
                                  value={unidad}
                                  onValueChange={(v) =>
                                    isBase
                                      ? setBaseUnidad(it.id, v as SimulatedUnidad, unidadOrig)
                                      : updateNuevo(it.id, "unidad", v as SimulatedUnidad)
                                  }
                                >
                                  <SelectTrigger
                                    className={`h-8 text-[12px] ${isBase && ed.unidad_medida ? "ring-1 ring-primary border-primary/40 bg-primary/5 font-semibold" : ""}`}
                                  >
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {UNIDADES.map((u) => (
                                      <SelectItem key={u} value={u} className="text-[12px]">{u}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                            <div className="col-span-4 md:col-span-1 space-y-1">
                              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Cant.</Label>
                              <NumericCell
                                value={num(cantidad)}
                                edited={isBase && ed.cantidad !== undefined}
                                min={0}
                                onChange={(n) =>
                                  isBase
                                    ? setBaseField(it.id, "cantidad", n, cantidadOrig, { recalcSubTotal: true, cantidadOriginal: cantidadOrig, fobOriginal: fobOrig })
                                    : updateNuevo(it.id, "cantidad", n)
                                }
                              />
                            </div>
                            <div className="col-span-4 md:col-span-2 space-y-1">
                              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Moneda</Label>
                              <CurrencyCombobox
                                compact
                                value={moneda}
                                onChange={(m) =>
                                  isBase
                                    ? setBaseMoneda(it.id, m, monedaOrig)
                                    : updateNuevo(it.id, "moneda" as keyof SimulatedItem, m as never)
                                }
                                className={isBase && (ed as { moneda?: string }).moneda ? "ring-1 ring-primary border-primary/40 bg-primary/5 font-semibold" : ""}
                              />
                            </div>
                            <div className="col-span-4 md:col-span-2 space-y-1">
                              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">FOB Unit.</Label>
                              <NumericCell
                                value={num(fob)}
                                edited={isBase && ed.precio_unitario !== undefined}
                                min={0}
                                onChange={(n) =>
                                  isBase
                                    ? setBaseField(it.id, "precio_unitario", n, fobOrig, { recalcSubTotal: true, cantidadOriginal: cantidadOrig, fobOriginal: fobOrig })
                                    : updateNuevo(it.id, "precio_unitario", n)
                                }
                              />
                            </div>

                            {isVisible(uiMode, "peso") && (
                              <div className="col-span-6 md:col-span-3 space-y-1">
                                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Peso (kg)</Label>
                                <NumericCell
                                  value={num(peso)}
                                  edited={isBase && ed.peso_bruto_kg !== undefined}
                                  min={0}
                                  onChange={(n) =>
                                    isBase
                                      ? setBaseField(it.id, "peso_bruto_kg", n, pesoOrig)
                                      : updateNuevo(it.id, "peso_bruto_kg", n)
                                  }
                                />
                              </div>
                            )}
                            {isVisible(uiMode, "cbm") && (
                              <div className="col-span-6 md:col-span-3 space-y-1">
                                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Vol. (CBM)</Label>
                                <NumericCell
                                  value={num(cbm)}
                                  edited={isBase && ed.volumen_cbm !== undefined}
                                  min={0}
                                  onChange={(n) =>
                                    isBase
                                      ? setBaseField(it.id, "volumen_cbm", n, cbmOrig)
                                      : updateNuevo(it.id, "volumen_cbm", n)
                                  }
                                />
                              </div>
                            )}
                            {isVisible(uiMode, "grados") && (
                              <div className="col-span-12 md:col-span-3 space-y-1">
                                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Tipo ICO</Label>
                                <Select
                                  value={icoTipo ?? "__BEBIDAS__"}
                                  onValueChange={(v) => {
                                    const next = v === "__BEBIDAS__" ? null : v;
                                    if (isBase) {
                                      setBaseIcoTipo(it.id, next, icoTipoOrig);
                                    } else {
                                      updateNuevo(it.id, "ico_tipo" as keyof SimulatedItem, next as never);
                                    }
                                  }}
                                >
                                  <SelectTrigger
                                    className={cn(
                                      "h-8 text-xs",
                                      isBase && (ed as any).ico_tipo !== undefined ? "ring-1 ring-primary" : "",
                                    )}
                                  >
                                    <SelectValue placeholder="Bebidas" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="__BEBIDAS__">Bebidas (g·l)</SelectItem>
                                    <SelectItem value="VEHICULOS">Vehículos (% CIF)</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                            {isVisible(uiMode, "grados") && !isIcoVehiculos && (
                              <div className="col-span-4 md:col-span-2 space-y-1">
                                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Grados</Label>
                                <NumericCell
                                  value={num(grados)}
                                  edited={isBase && (ed as any).grados_alcoholicos !== undefined}
                                  min={0}
                                  step="0.1"
                                  onChange={(n) =>
                                    isBase
                                      ? setBaseField(it.id, "grados_alcoholicos" as any, n, gradosOrig)
                                      : updateNuevo(it.id, "grados_alcoholicos" as any, n as any)
                                  }
                                />
                              </div>
                            )}
                            {isVisible(uiMode, "litros") && !isIcoVehiculos && (
                              <div className="col-span-4 md:col-span-2 space-y-1">
                                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Litros</Label>
                                <NumericCell
                                  value={num(litros)}
                                  edited={isBase && (ed as any).litros_por_unidad !== undefined}
                                  min={0}
                                  step="0.01"
                                  onChange={(n) =>
                                    isBase
                                      ? setBaseField(it.id, "litros_por_unidad" as any, n, litrosOrig)
                                      : updateNuevo(it.id, "litros_por_unidad" as any, n as any)
                                  }
                                />
                              </div>
                            )}
                            {isVisible(uiMode, "tarifaIco") && !isIcoVehiculos && (
                              <div className="col-span-4 md:col-span-2 space-y-1">
                                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Tarifa ICO</Label>
                                <NumericCell
                                  value={num(tarifaIco)}
                                  edited={isBase && (ed as any).tarifa_ico !== undefined}
                                  min={0}
                                  step="1"
                                  onChange={(n) =>
                                    isBase
                                      ? setBaseField(it.id, "tarifa_ico" as any, n, tarifaIcoOrig)
                                      : updateNuevo(it.id, "tarifa_ico" as any, n as any)
                                  }
                                />
                              </div>
                            )}
                            {isVisible(uiMode, "grados") && isIcoVehiculos && (
                              <div className="col-span-12 md:col-span-6 space-y-1">
                                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">ICO Vehículo</Label>
                                <div className="h-8 px-2 flex items-center rounded-md border border-dashed bg-muted/30 text-[11px] text-muted-foreground">
                                  Calculado automáticamente como % sobre el CIF (Costo + Seguro + Flete) según el rango de valor CIF.
                                </div>
                              </div>
                            )}
                            {isVisible(uiMode, "arancel") && (
                              <div className="col-span-6 md:col-span-2 space-y-1">
                                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">% Arancel</Label>
                                <NumericCell
                                  value={arancel}
                                  edited={arancelOv[it.id] !== undefined}
                                  min={0}
                                  onChange={(n) => setRatePct("arancel", it.id, n, isBase ? arancelBase : -1)}
                                />
                              </div>
                            )}
                            {isVisible(uiMode, "iva") && (
                              <div className="col-span-6 md:col-span-2 space-y-1">
                                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">% IVA</Label>
                                <NumericCell
                                  value={iva}
                                  edited={ivaOv[it.id] !== undefined}
                                  min={0}
                                  onChange={(n) => setRatePct("iva", it.id, n, isBase ? ivaBase : -1)}
                                />
                              </div>
                            )}
                            {isVisible(uiMode, "beneficio") && (
                              <div className="col-span-12 md:col-span-8 space-y-1">
                                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Régimen aduanero</Label>
                                <BenefitAduaneroSelect
                                  value={benefit}
                                  mode="controlled"
                                  variant="compact"
                                  onChange={(next) => setBenefit(it.id, next, isBase ? benefitBase : undefined)}
                                  className={beneficioOv[it.id] !== undefined ? "ring-1 ring-primary rounded-md" : ""}
                                />
                              </div>
                            )}
                            <div className="col-span-12 md:col-span-4 space-y-1">
                              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Sub-Total</Label>
                              {(() => {
                                const rate = moneda === "COP" ? 1 : Number(trmEfectivaMap?.[moneda] ?? 0);
                                const subProjCOP = subProj * rate;
                                const subOrigCOP = subOrig * rate;
                                return (
                                  <div className="h-auto min-h-8 px-3 py-1 flex flex-col items-end justify-center rounded-md border bg-muted/30 text-right text-[13px] tabular-nums font-mono font-medium">
                                    <div>{rate > 0 ? fmtCOP(subProjCOP) : fmtMoney(subProj, moneda)}</div>
                                    <div className="text-[10px] text-muted-foreground">{fmtMoney(subProj, moneda)}</div>
                                    {isBase && dirty && Math.abs(subProj - subOrig) > 0.005 && rate > 0 && (
                                      <div className="text-[10px] text-muted-foreground">base {fmtCOP(subOrigCOP)}</div>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                          <div className="flex flex-col gap-1 mt-5">
                            {isBase && dirty && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-muted-foreground hover:text-foreground"
                                onClick={() => resetRow(it.id)}
                                title="Restaurar valores originales"
                              >
                                <RotateCcw className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-foreground"
                              onClick={() =>
                                duplicateItem({
                                  sku,
                                  descripcion,
                                  unidad,
                                  cantidad: num(cantidad),
                                  precio_unitario: num(fob),
                                  peso_bruto_kg: num(peso),
                                  volumen_cbm: num(cbm),
                                  hs_code: hsCode,
                                  grados_alcoholicos: num(grados),
                                  litros_por_unidad: num(litros),
                                  tarifa_ico: num(tarifaIco),
                                  ico_tipo: (icoTipo as string) || null,
                                  beneficio_aduanero: benefit,
                                  moneda,
                                })
                              }
                              title="Duplicar ítem"
                            >
                              <Copy className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-destructive"
                              onClick={() =>
                                setConfirmDelete({ id: it.id, sku: sku || "(sin SKU)", isBase })
                              }
                              title="Eliminar ítem"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </div>
        </div>

        <SheetFooter className="px-6 py-3 border-t bg-muted/20 flex-row sm:justify-between items-center gap-2">
          <div className="text-[11px] text-muted-foreground tabular-nums">
            {editedCount} edit. · {nuevosValidos.length} nuevo{nuevosValidos.length === 1 ? "" : "s"} válido
            {nuevosValidos.length === 1 ? "" : "s"}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button size="sm" onClick={handleApply} className="gap-1.5">
              Aplicar al escenario
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
      <AlertDialog open={confirmDelete !== null} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este ítem?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.isBase
                ? `El ítem "${confirmDelete?.sku}" se inhabilitará en este escenario (no se elimina del snapshot original).`
                : `El ítem "${confirmDelete?.sku}" se eliminará de la lista.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!confirmDelete) return;
                if (confirmDelete.isBase) {
                  disableBaseItem(confirmDelete.id);
                } else {
                  removeNuevo(confirmDelete.id);
                }
                setConfirmDelete(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  );
}
